// Direct notification test that bypasses Firebase SDK CORS issues
import { createLogger } from './logger';

const logger = createLogger('TestNotificationDirect');

/**
 * Parse notification HTTP response into a consistent result shape.
 * @param {Response} response - Fetch response
 * @returns {Promise<{ success: boolean, data?: object, error?: string, message?: string, status?: number }>}
 */
async function parseNotificationResponse(response) {
  if (response.ok) {
    const result = await response.json();
    const ok = result?.success === true;
    if (ok) return { success: true, data: result };
    return {
      success: false,
      data: result,
      error: result?.error || result?.message,
      message: result?.message
    };
  }
  const errorText = await response.text();
  return {
    success: false,
    error: `HTTP ${response.status}: ${errorText}`,
    status: response.status
  };
}

/**
 * Test notification using direct HTTP call with proper CORS handling
 */
export const testNotificationDirect = async (currentUser) => {
  if (!currentUser) {
    logger.warn('No current user available for testing');
    return { success: false, message: 'No user logged in' };
  }

  try {
    logger.info('Testing notification with direct HTTP call...');
    
    // Use the new HTTP endpoint with CORS support
    const functionUrl = 'https://sendorderstatusnotificationhttp-k6hua22erq-uc.a.run.app';
    
    const testData = {
      userId: currentUser.uid,
      orderId: 'direct-test-' + Date.now(),
      status: 'test',
      message: 'Direct HTTP test notification from PackMyCart'
    };
    
    logger.info('Sending direct HTTP request with data:', testData);
    
    // Use fetch with proper headers for CORS and timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    let response;
    try {
      response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(testData), // Direct JSON body for HTTP endpoint
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
    
    logger.info('Direct HTTP response status:', response.status);
    const parsed = await parseNotificationResponse(response);
    if (!parsed.success) {
      logger.error('Direct HTTP test failed:', response.status, parsed.error);
    } else {
      logger.info('Direct HTTP test response:', parsed.data);
    }
    return parsed;
    
  } catch (error) {
    logger.error('Direct HTTP test error:', error);
    return { 
      success: false, 
      error: error.name === 'AbortError' ? 'Request timeout (30s)' : error.message,
      type: error.name === 'AbortError' ? 'timeout' : 'network_error'
    };
  }
};

/**
 * Test if the Cloud Function endpoint is accessible
 */
export const testFunctionEndpoint = async () => {
  try {
    const functionUrl = 'https://sendorderstatusnotificationhttp-k6hua22erq-uc.a.run.app';
    
    logger.info('Testing function endpoint accessibility:', functionUrl);
    
    // Try a simple OPTIONS request first (CORS preflight)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout for OPTIONS
    
    const optionsResponse = await fetch(functionUrl, {
      method: 'OPTIONS',
      headers: {
        'Origin': window.location.origin,
        'Access-Control-Request-Method': 'POST',
        'Access-Control-Request-Headers': 'Content-Type'
      },
      mode: 'cors',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    logger.info('OPTIONS response status:', optionsResponse.status);
    logger.info('OPTIONS response headers:', Object.fromEntries(optionsResponse.headers.entries()));
    
    return {
      success: optionsResponse.ok,
      status: optionsResponse.status,
      headers: Object.fromEntries(optionsResponse.headers.entries()),
      corsEnabled: optionsResponse.headers.get('Access-Control-Allow-Origin') !== null
    };
    
  } catch (error) {
    logger.error('Function endpoint test error:', error);
    return { 
      success: false, 
      error: error.name === 'AbortError' ? 'Request timeout (10s)' : error.message,
      type: error.name === 'AbortError' ? 'timeout' : 'network_error'
    };
  }
};

/**
 * Test notification with alternative approach
 */
export const testNotificationAlternative = async (currentUser) => {
  if (!currentUser) {
    return { success: false, message: 'No user logged in' };
  }

  try {
    // Try using a different approach - simulate what the Firebase SDK should do
    const functionUrl = 'https://sendorderstatusnotificationhttp-k6hua22erq-uc.a.run.app';
    
    const testData = {
      userId: currentUser.uid,
      orderId: 'alt-test-' + Date.now(),
      status: 'test',
      message: 'Alternative test notification'
    };
    
    // Use direct JSON for HTTP endpoint with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(testData),
      mode: 'cors',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return await parseNotificationResponse(response);
  } catch (error) {
    return { 
      success: false, 
      error: error.name === 'AbortError' ? 'Request timeout (30s)' : error.message,
      type: error.name === 'AbortError' ? 'timeout' : 'network_error'
    };
  }
};
